import { useState } from 'react';
import type { GuestMe, PublicGuest } from '@karozik/shared';
import { Avatar, cn, FACE_MODE_LABELS, FaceMashup, useNow, type PartyConnection } from '@karozik/ui';
import { SelfieCard } from './SelfieCard';

const CHOICE_STYLES = ['bg-pink', 'bg-violet', 'bg-cyan text-night', 'bg-sun text-night'];
const LETTERS = ['A', 'B', 'C', 'D'];

function PairLabel({ pair, big }: { pair: [PublicGuest, PublicGuest]; big: boolean }) {
  return (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <Avatar guest={pair[0]} size={big ? 'md' : 'sm'} />
      <span className="truncate">{pair[0].pseudo}</span>
      <span className="opacity-60">+</span>
      <Avatar guest={pair[1]} size={big ? 'md' : 'sm'} />
      <span className="truncate">{pair[1].pseudo}</span>
    </span>
  );
}

/** Full-screen Têtes Mêlées answer pad. Shown only while the game runs. */
export function FacesOverlay({ conn, me, big, joinToken }: { conn: PartyConnection; me: GuestMe | null; big: boolean; joinToken?: string }) {
  const now = useNow(250) + conn.clockSkew;
  const f = conn.state?.faces;
  const [error, setError] = useState<{ round: number; message: string } | null>(null);
  if (!f) return null;
  const playS = (f.playMs + f.sharpMs) / 1000;
  const left = Math.min(f.stage === 'playing' ? playS : 99, Math.max(0, Math.ceil((f.stageEndsAt - now) / 1000)));
  const lastChance = f.stage === 'playing' && f.startedAt != null && now >= f.startedAt + f.playMs;
  const answered = !!me && f.answered.includes(me.id);
  const myPoints = me ? f.lastPoints[me.id] : undefined;
  const myRank = me ? f.leaderboard.findIndex((e) => e.guest.id === me.id) : -1;
  const roundError = error?.round === f.round ? error.message : null;
  const right = f.correctIndex != null && f.choices ? f.choices[f.correctIndex] : null;
  const pictured = !!me && !!right && right.some((g) => g.id === me.id);

  return (
    <div className="fixed inset-0 z-40 flex flex-col overflow-y-auto bg-night px-5 pt-6 safe-bottom">
      <div className={cn('mx-auto flex w-full flex-1 flex-col', big ? 'max-w-3xl' : 'max-w-md')}>
        <div className="flex items-center justify-between">
          <div className="font-display text-xl font-bold text-gradient">Têtes Mêlées</div>
          <div className="text-sm text-ink-dim">{f.round > 0 && f.stage !== 'final' && `Manche ${f.round}/${f.totalRounds}`}</div>
        </div>

        {f.stage === 'intro' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-5 text-center">
            <div className="text-7xl">🤪</div>
            <p className="text-xl font-semibold">Deux têtes de la soirée, mélangées sur l’écran. Trouve qui se cache dedans, le plus vite possible !</p>
            <div className="font-display text-7xl font-bold">{left}</div>
            {joinToken && (
              <div className="w-full text-left">
                <SelfieCard joinToken={joinToken} compact />
              </div>
            )}
          </div>
        )}

        {f.stage === 'playing' && f.choices && (
          <div className="flex flex-1 flex-col">
            <div className="my-3 h-2 overflow-hidden rounded-full bg-surface-2">
              <div className="bg-gradient-neon h-full transition-all duration-300" style={{ width: `${(left / playS) * 100}%` }} />
            </div>
            <FaceMashup faces={f} durationMs={f.playMs} clockSkew={conn.clockSkew} sharp={false} className={cn('mx-auto rounded-2xl', big ? 'h-[38vh]' : 'h-[30vh]')} />
            <p className={cn('mt-2 text-center text-xs tracking-widest uppercase', lastChance ? 'font-bold text-sun' : 'text-ink-faint')}>
              {lastChance ? `Image révélée : dernière chance ! ${left} s` : f.mode && FACE_MODE_LABELS[f.mode]}
            </p>
            {answered ? (
              <div className="flex flex-1 flex-col items-center justify-center text-center">
                <div className="text-5xl">🤞</div>
                <p className="mt-2 text-xl font-semibold">Réponse enregistrée !</p>
                <p className="text-ink-dim">Regarde l’écran géant…</p>
              </div>
            ) : roundError ? (
              <div className="flex flex-1 flex-col items-center justify-center text-center">
                <p className="text-xl font-semibold">{roundError}</p>
              </div>
            ) : (
              <div className="mt-3 grid flex-1 gap-2.5 pb-4" style={{ gridTemplateRows: `repeat(${f.choices.length}, minmax(3.5rem, 1fr))` }}>
                {f.choices.map((pair, i) => (
                  <button
                    key={i}
                    disabled={!me}
                    onClick={async () => {
                      const r = await conn.send('faces:answer', { choice: i });
                      setError(r.ok ? null : { round: f.round, message: r.error ?? 'Erreur' });
                    }}
                    className={cn('flex min-h-14 items-center gap-3 rounded-2xl px-4 text-left font-bold shadow-lg transition active:scale-95', CHOICE_STYLES[i], big ? 'text-2xl' : 'text-base')}
                  >
                    <span className="font-display text-2xl opacity-70">{LETTERS[i]}</span>
                    <PairLabel pair={pair} big={big} />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {f.stage === 'reveal' && (
          <div className="flex flex-1 flex-col items-center justify-center text-center">
            <FaceMashup faces={f} durationMs={f.playMs} clockSkew={conn.clockSkew} sharp className={cn('rounded-2xl', big ? 'h-[40vh]' : 'h-[34vh]')} />
            {right && (
              <div className="font-display mt-4 text-2xl font-bold">
                {right[0].pseudo} + {right[1].pseudo}
              </div>
            )}
            <div className={cn('mt-5 rounded-2xl px-6 py-3 text-xl font-bold', myPoints ? 'bg-lime text-night' : 'bg-surface-2')}>
              {pictured ? 'C’était toi ! 😄' : myPoints ? `+${myPoints} points 🎉` : answered ? 'Raté… 😅' : 'Pas de réponse'}
            </div>
          </div>
        )}

        {f.stage === 'final' && (
          <div className="flex flex-1 flex-col items-center justify-center">
            <div className="text-6xl">🏆</div>
            <h2 className="font-display mt-2 text-2xl font-bold">Classement</h2>
            <ol className="mt-4 w-full space-y-2">
              {f.leaderboard.slice(0, 5).map((e, i) => (
                <li key={e.guest.id} className={cn('flex items-center gap-3 rounded-2xl bg-surface px-4 py-3', e.guest.id === me?.id && 'ring-2 ring-lime')}>
                  <span className="w-6 font-bold">{['🥇', '🥈', '🥉'][i] ?? i + 1}</span>
                  <Avatar guest={e.guest} size="sm" />
                  <span className="flex-1 truncate font-semibold">{e.guest.pseudo}</span>
                  <span className="font-bold">{e.points}</span>
                </li>
              ))}
              {f.leaderboard.length === 0 && <li className="text-center text-ink-dim">Personne n’a marqué cette fois-ci !</li>}
            </ol>
            {myRank > 4 && <p className="mt-4 text-ink-dim">Tu es {myRank + 1}ᵉ !</p>}
          </div>
        )}
      </div>
    </div>
  );
}
